import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormatting } from 'locale';
import { getClientId } from 'utils';
import { Product } from 'core/types/api/Api';
import { INVESTOR_PROFILE_CONTACT_ID, THREEBTYPES } from 'constants/constants';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useProductAllocation } from '../../../../../hooks/useProductAllocation';
import { LiquidityId } from '../../../../../../../../../constants/instrument';
import { PORTFOLIO_BLOCKED_ID, PORTFOLIO_CLOSED_ID } from '../../../../../../../../../constants/portfolioStatuses';
import { parseXml } from '../../../../../../../../../utils/xmlParser';
import {
    BodyItemType,
    FooterItemType,
    IndividualPortfolioDataType,
} from '../../../../../../../../../ViewModels/ModelPortfolioDetails';
import { useCachedAPI } from '../../../../../../../../../hooks/useCachedAPI';
import { adaptAllocations } from '../adapters/adaptProposalData';
import { riskCategoriesByLang } from '../../../../../constants';
import { useModelList } from '../../../../../hooks/useModelList';

enum FeeType {
    Asset = 1,
    Custody = 0,
    Entry = 2,
}

type Fee = {
    discountValue: number | null,
    expiryDate: string | null,
    fee: number | null,
    feeType: FeeType,
    startDate: string | null
}

export const useStrategy = ({ dfsClientId, portfolioId }) => {
    const { i18n: { language }, t } = useTranslation();
    const clientId = getClientId(dfsClientId);
    const { pathname } = useLocation();

    const [portfolio, setPortfolio] = useState<any>(null);
    const [lastApplicationDetails, setLastApplicationDetails] = useState<any>({});
    const [isIndividualPortfolio, setIndividualPortfolio] = useState<boolean>(false);

    const [modelPortfolioId, setModelPortfolioId] = useState();
    const [productId, setProductId] = useState();
    const [modelPortfolio, setModelPortfolio] = useState<any>();

    const [positions, setPositions] = useState([]);
    const [isRebalancing, setRebalancing] = useState(null);

    const [isInitLoading, setInitLoading] = useState<boolean>(true);
    const [individualPortfolioData, setIndividualPortfolioData] = useState<IndividualPortfolioDataType<Partial<BodyItemType>, Partial<FooterItemType>>>({
        body: [],
        footer: {},
    });

    const [strategyError, setStrategyError] = useState<any>();
    const [portfolioProducts, setPortfolioProducts] = useState<Partial<Product & { factsheetUrl: string }>[]>([]);
    const [fees, setFees] = useState<Array<Fee>>([]);
    const [investmentAppPersonalDetails, setInvestmentAppPersonalDetails] = useState(null);
    const [withdrawal, setWithdrawal] = useState(null);
    const [investmentAppId, setInvestmentAppId] = useState(null);
    const [withdrawalData, setWithdrawalData] = useState(null);

    const {
        getPortfolioDetails,
        getLastApplicationByPortfolio,
        getInvestmentApplication,
        getModelPortfolio,
        getPortfolioProposal,
        getWithdrawalData,
    } = useCachedAPI();

    // -------------------------
    // DATA FETCH
    // -------------------------
    const getData = useCallback(async () => {
        try {
            setInitLoading(true);

            const [
                portfolioDetails,
                lastApplicationByPortfolio,
                withdrawalDataResponse,
            ] = await Promise.all([
                getPortfolioDetails([portfolioId, clientId, { language }, { withCache: true }]),
                getLastApplicationByPortfolio([{ portfolioId, language }, { withCache: true }]),
                getWithdrawalData([{ portfolioId, language }, { withCache: true }]),
            ]);

            const investmentApplicationId = lastApplicationByPortfolio?.investmentApplicationId;

            const investmentApplicationData = await getInvestmentApplication([
                { investmentApplicationId },
            ]);

            sessionStorage.setItem(
                INVESTOR_PROFILE_CONTACT_ID,
                investmentApplicationData?.investmentDescription?.investorProfileContactId,
            );

            const portfolioProposalId = investmentApplicationData?.investmentDescription?.portfolioProposalId;

            const mpId = investmentApplicationData?.investmentDescription?.selectStrategy?.modelPortfolioId;

            const modelPortfolioData = await getModelPortfolio([mpId, { language }, { withCache: true }]);

            const isIndividual = !!portfolioDetails?.PortfolioProposalId;

            setIndividualPortfolio(isIndividual);

            if (isIndividual) {
                const portfolioProposal = await getPortfolioProposal([
                    clientId,
                    portfolioProposalId,
                    { language },
                    { withCache: true },
                ]);

                const portfolioProposalPositions = portfolioProposal?.Positions || [];

                setPositions(portfolioProposalPositions);
                setIndividualPortfolioData(adaptAllocations(portfolioProposalPositions));
            } else {
                setPositions(portfolioDetails?.Positions || []);
            }

            setWithdrawalData(withdrawalDataResponse);
            setPortfolio(portfolioDetails);
            setLastApplicationDetails(lastApplicationByPortfolio);
            setProductId(portfolioDetails?.Product?.Id);
            setRebalancing(
                investmentApplicationData?.investmentDescription?.selectStrategy?.rebalancing,
            );
            setModelPortfolio(modelPortfolioData);
            setModelPortfolioId(mpId);
            setFees(investmentApplicationData?.investmentDescription?.fees || []);
            setInvestmentAppPersonalDetails(
                investmentApplicationData?.investmentDescription?.personalDetails,
            );
            setWithdrawal(investmentApplicationData?.investmentDescription?.withdrawal);
            setInvestmentAppId(investmentApplicationId);
        } catch (error) {
            setStrategyError(error);
        } finally {
            setInitLoading(false);
        }
    }, [portfolioId, clientId, language]);

    useEffect(() => {
        setStrategyError(undefined);
        getData();
    }, [portfolioId, clientId, language]);

    // -------------------------
    // CONTACT GROUP
    // -------------------------

    const {
        data: contactGroupDetailsData,
    } = useGetCustomService({
        service: 'customClientService',
        api: 'getContactGroupDetails',
        payload: { groupId: portfolio?.ContactGroupId },
        enabled: !!portfolio?.ContactGroupId,
    });

    const isBO = clientId ? !!contactGroupDetailsData?.data?.owners?.[clientId] : false;

    const isPortfolioOnReadOnly = portfolio?.PortfolioStatusId !== PORTFOLIO_CLOSED_ID
        && portfolio?.PortfolioStatusId !== PORTFOLIO_BLOCKED_ID;

    const isPortfolioChangeable = isPortfolioOnReadOnly
        && portfolio?.Product?.ExternalId
        && isBO
        && ![THREEBTYPES.zic, THREEBTYPES.pk].includes(
            portfolio?.Product?.ExternalId,
        );

    // -------------------------
    // CHANGE PLAN
    // -------------------------
    const { data: changePlanResponse, isLoading: getOngoingChangePlanInProgress } = useGetCustomService({
        service: 'planService',
        api: 'checkIfChangePlanInProgress',
        payload: { portfolioId },
        enabled: !!(isPortfolioChangeable && pathname?.includes('/strategy')),
        staleTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const ongoingPlan = useMemo(() => {
        const changePlanData = changePlanResponse?.data;

        if (!changePlanData) return null;

        return {
            current: parseXml(changePlanData.currentModelPortfolio)?.root,
            submitted: parseXml(changePlanData.submitedModelPortfolio)?.root,
            portfolioId,
        };
    }, [changePlanResponse?.data, portfolioId]);

    // -------------------------
    // MODEL LIST
    // -------------------------
    const { strategies, products } = useModelList({ productId });

    // -------------------------
    // PRODUCTS
    // -------------------------
    useEffect(() => {
        if (isIndividualPortfolio && products.length && positions.length && modelPortfolio) {
            try {
                const chosenSecurityIds: number[] = [];

                const portfoliosPositions = positions
                    .filter((p: any) => p?.Security?.Type?.Id !== LiquidityId)
                    .map((p: any) => ({
                        allocation: `${Math.round(p?.Allocation * 100)}%`,
                        name: p?.Security?.Name ?? '-',
                        positionId: p?.Id,
                        securityId: p?.Security?.Id,
                        modelPortfolioId: p?.Id,
                        factsheetUrl: p?.Factsheet?.replace('url:', ''),
                    }))
                    .filter(item => {
                        if (chosenSecurityIds.includes(item.securityId)) return false;
                        chosenSecurityIds.push(item.securityId);

                        return true;
                    });

                setPortfolioProducts(portfoliosPositions);
            } catch (error) {
                setStrategyError(error);
            }
        }
    }, [positions, products, isIndividualPortfolio]);

    useEffect(() => {
        if (!isIndividualPortfolio && modelPortfolio) {
            setPortfolioProducts([
                {
                    name: modelPortfolio?.Name,
                    factsheetUrl: modelPortfolio?.Factsheet?.replace('url:', ''),
                },
            ]);
        }
    }, [isIndividualPortfolio, modelPortfolio]);

    // -------------------------
    // ALLOCATIONS
    // -------------------------
    const { getFormattedXAxisFormat } = useFormatting();

    const {
        allocations,
        isLoading: isAllocationsLoading,
        factsheetUrl,
        error: allocationError,
        riskScore,
    } = useProductAllocation({
        productId: modelPortfolioId,
        individualPortfolioData,
    });

    const isLoading = isAllocationsLoading
        || getOngoingChangePlanInProgress
        || isInitLoading;

    const error = strategyError || allocationError;

    const strategyName = portfolio?.RiskCategory?.Name;
    const modelPortfolioDescription = modelPortfolio?.Description;

    const getSelectStrategy = () => Number(riskScore);

    const riskCategories = useMemo(() => riskCategoriesByLang(t), [t]);

    const riskName = useMemo(() => {
        const risk = strategies?.find(({ Id }) => riskScore === Id - 1);

        return risk?.Name ?? '';
    }, [riskScore, strategies]);

    return {
        t,
        riskName,
        modelPortfolioDescription,
        modelPortfolio,
        isPortfolioChangeable,
        isPortfolioOnReadOnly,
        portfolioProducts,
        isIndividualPortfolio,
        clientId,
        strategyName,
        riskCategories,
        getSelectStrategy,
        getFormattedXAxisFormat,
        allocations,
        factsheetUrl,
        isRebalancing,
        error,
        isLoading,
        portfolioData: portfolio,
        lastApplicationDetails,
        ongoingPlan,
        fees,
        setFees,
        investmentAppPersonalDetails,
        withdrawal,
        withdrawalData,
        investmentAppId,
    };
};
